import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Grid, Group, LoadingOverlay } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { MainLayout } from '../../layouts'
import { ContentBox, BackListButton, SaveButton, ResetButton } from '../../partials'
import { FormTypes } from '../../../types'
import { useAppDispatch, useAppSelector } from '../../../store'
import { IVehicleParams, VehicleType } from '../../../store/types'
import { IFormRef } from '../../../types'
import { getFormattedValidationErrors } from '../../../utils/mixins'
import Form from './Form'
import { fetchShowVehicle, resetShowVehicle } from '../../../store/features/vehicles/showVehicleSlice'
import { fetchUpdateVehicle, resetUpdateVehicle } from '../../../store/features/vehicles/updateVehicleSlice'

const Edit = () => {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const formRef = useRef<IFormRef>(null)
  const dispatch = useAppDispatch()
  const [data, setData] = useState<any | null>(null)

  const {
    isLoading: userIsLoading,
    response: userResponse,
    error: userError
  } = useAppSelector(state => state.showVehicle)
  const { isLoading, response, error } = useAppSelector(state => state.updateVehicle)
  useEffect(() => {
    if (userResponse === null && data === null) {
      ;(async () => {
        await dispatch(fetchShowVehicle(id as string))
      })()
    }
  }, [userResponse, data, id, dispatch])

  const getSerializedData = (data: VehicleType) => {
    return {
      company: data.company.id,
      location: data.location,
      vin: data.vin,
      unit: data.unit,
      brand_name: data.brand.name,
      model_name: data.model.name,
      model_detail_name: data.model_detail.name,
      mileage: data.mileage,
      group_name: data.group_name,
      class_name: data.class_name,
      model_year: data.model_year,
      color: data.color,
      condition: data.condition,
      in_service_date: data.in_service_date,
      tags: data.tags
    }
  }

  useEffect(() => {
    if (userResponse !== null) {
      setData(getSerializedData(userResponse.data))

      if (data !== null) {
        dispatch(resetShowVehicle())
      }
    }
  }, [userResponse, data, dispatch])

  useEffect(() => {
    if (userError !== null) {
      if (userError.response.status === 401 && id) {
        ;(async () => {
          dispatch(resetShowVehicle())
          await dispatch(fetchShowVehicle(id))
        })()
      } else if (userError.response.status === 404) {
        dispatch(resetShowVehicle())
        showNotification({
          color: 'red',
          title: t('error'),
          message: t(userError.data.message)
        })

        navigate('/vehicles')
      } else {
        dispatch(resetShowVehicle())
        showNotification({
          color: 'red',
          title: t('error'),
          message: t('errors.unknown')
        })

        navigate('/vehicles')
      }
    }
  }, [userError, id, dispatch, t, navigate])

  useEffect(() => {
    if (response !== null) {
      setData(getSerializedData(response.data))

      showNotification({
        color: 'green',
        title: t('successful'),
        message: t('updated')
      })

      dispatch(resetUpdateVehicle())
    }
  }, [response, t, dispatch])

  useEffect(() => {
    if (error !== null) {
      if (error.response.status === 422) {
        formRef.current!.setErrors(getFormattedValidationErrors(error))
      } else {
        showNotification({
          color: 'red',
          title: t('unsuccessful'),
          message: error.response.data ? error.response.data.message : t('errors.unknown')
        })
      }
    }
  }, [error, t])

  useEffect(() => {
    if (response !== null) {
      dispatch(resetUpdateVehicle())
      formRef.current!.reset()
    }
  }, [response, dispatch])

  const handleSubmit = async (params: IVehicleParams) => {
    await dispatch(fetchUpdateVehicle({ id: id as string, params }))
  }

  return (
    <MainLayout>
      <LoadingOverlay visible={userIsLoading || isLoading || !data} />
      {data && (
        <ContentBox title={t('content_actions.create')}>
          <Form
            id="edit-vehicle-form"
            type={FormTypes.EDIT}
            ref={formRef}
            initialValues={data}
            onSubmit={handleSubmit}
          />
        </ContentBox>
      )}
      <ContentBox>
        <Grid>
          <Grid.Col span={6}>
            <BackListButton to="/vehicles" />
          </Grid.Col>
          <Grid.Col span={6}>
            <Group position="right">
              <SaveButton form="edit-vehicle-form" />
              <ResetButton form="edit-vehicle-form" />
            </Group>
          </Grid.Col>
        </Grid>
      </ContentBox>
    </MainLayout>
  )
}

export default Edit
